/**
 * Servizio per la gestione dello stato globale dell'applicazione.
 * Utilizza Angular Signals nativi per gestire lo stato e sincronizzarlo con il localStorage.
 * Gestisce autenticazione e profili utente.
 */
import {
  afterNextRender,
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { IToken } from '@core/models/auth.model';
import { IProfilo, IUser, RuoloAttivo, User } from '@core/models/user.model';
import { DeviceIdService } from '../device-id.service';
import { Console } from '../logger.service';

/**
 * Interfaccia che definisce la struttura dello stato globale dell'applicazione.
 *
 * Contiene tutti i dati necessari per la gestione di:
 * - autenticazione
 * - profili utente (integrati nell'utente)
 */
export interface IAuthStore {
  /** Token di autenticazione dell'utente */
  token: IToken | null;
  /** Dati dell'utente attualmente autenticato */
  user: IUser | null;
  /** Username dell'utente loggato */
  username: string | null;
  /** Eventuali errori dell'applicazione */
  error: any;
  /** Stato di caricamento dell'applicazione */
  isLoading: boolean;
  /** Identificativo univoco del dispositivo */
  device_id: string | null;
  /** Flag che indica se l'utente ha già visto l'intro */
  hasSeenIntro: boolean;
  /** Ruolo attualmente attivo per l'utente */
  activeRole: RuoloAttivo | null;
  /** Flag che indica se l'utente vuole essere ricordato */
  rememberMe: boolean;
}

/**
 * Stato iniziale dello store dell'applicazione.
 *
 * Tutti i valori sono impostati a null o array vuoti per garantire
 * un avvio pulito dell'applicazione.
 */
const initialState: IAuthStore = {
  token: null,
  user: null,
  username: null,
  error: null,
  isLoading: false,
  device_id: null,
  hasSeenIntro: false,
  activeRole: null,
  rememberMe: false,
};

/**
 * Genera una chiave univoca per il localStorage basata sull'URL dell'app.
 * Questo evita che il localStorage venga pulito quando altre app sullo stesso dominio fanno logout.
 * Usa direttamente l'origin dell'app come chiave (es. "http://localhost:4200")
 */
function getStorageKey(): string {
  // Usa direttamente l'origin dell'app come chiave (formato: http://accounts.google.com)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://auth-app';
  return origin;
}

/**
 * Chiave per il localStorage (univoca per ogni app/origine)
 * Formato: http://localhost:4200 o http://accounts.google.com
 */
const STORAGE_KEY = getStorageKey();

/**
 * Store globale dell'applicazione basato su Angular Signals nativi.
 *
 * Questo store gestisce tutto lo stato dell'applicazione utilizzando Angular Signals
 * per una gestione reattiva e performante. Include sincronizzazione automatica
 * con il localStorage per persistere i dati tra le sessioni.
 *
 * **Caratteristiche principali:**
 * - Gestione dell'autenticazione e dei token
 * - Gestione dei profili utente (integrati nell'utente) e ruoli attivi
 * - Sincronizzazione automatica con localStorage
 * - Proprietà computed per stati derivati
 * - Inizializzazione automatica del device ID
 *
 * **Utilizzo:**
 * ```typescript
 * constructor(private appStore: AppStore) {}
 *
 * // Lettura dello stato
 * const isAuthenticated = this.appStore.isAuthenticated();
 * const currentUser = this.appStore.user();
 *
 * // Modifica dello stato
 * this.appStore.setUser(newUser);
 * this.appStore.setToken(authToken);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AppStore {
  private readonly deviceIdService = inject(DeviceIdService);
  private readonly destroyRef = inject(DestroyRef);

  /** Incrementato quando cambia `prefers-color-scheme` e la preferenza è `auto` (invalida `effectiveTheme`). */
  private readonly colorSchemeTick = signal(0);

  /** Preferenza tema: auto segue il sistema; light/dark forzano `data-theme` su `<html>`. */
  readonly themePreference = signal<'auto' | 'light' | 'dark'>('auto');

  /** Tema effettivo dopo risoluzione di `auto` + matchMedia. */
  readonly effectiveTheme = computed<'light' | 'dark'>(() => {
    this.colorSchemeTick();
    const pref = this.themePreference();
    if (pref === 'light') {
      return 'light';
    }
    if (pref === 'dark') {
      return 'dark';
    }
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Signals per lo stato
  readonly token = signal<IToken | null>(null);
  readonly user = signal<IUser | null>(null);
  readonly username = signal<string | null>(null);
  readonly error = signal<any>(null);
  readonly isLoading = signal<boolean>(false);
  readonly device_id = signal<string | null>(null);
  readonly hasSeenIntro = signal<boolean>(false);
  readonly activeRole = signal<RuoloAttivo | null>(null);
  readonly rememberMe = signal<boolean>(false);
  readonly profiles = signal<IProfilo[] | null>(null);

  // Computed properties
  /**
   * Computed property che indica se l'utente è autenticato.
   * @returns True se sono presenti sia token che user
   */
  readonly isAuthenticated = computed(() => Boolean(this.token() && this.user()));

  /**
   * Computed property che indica se è presente uno username.
   * @returns True se lo username è presente e non vuoto
   */
  readonly hasUsername = computed(() => Boolean(this.username()));

  /**
   * Computed property che indica se è presente un device ID.
   * @returns True se il device ID è presente e non vuoto
   */
  readonly hasDeviceId = computed(() => Boolean(this.device_id()));

  constructor() {
    this.initializeStore();
    this.setupStorageSync();
    this.setupThemeSync();
  }

  /**
   * Imposta la preferenza tema e la persiste nello stato salvato sotto la chiave root `STORAGE_KEY`.
   */
  setThemePreference(preference: 'auto' | 'light' | 'dark'): void {
    this.themePreference.set(preference);
    this.saveToStorage();
  }

  private setupThemeSync(): void {
    effect(() => {
      const theme = this.effectiveTheme();
      if (typeof document === 'undefined') {
        return;
      }
      document.documentElement.setAttribute('data-theme', theme);
    });

    afterNextRender(() => {
      if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
        return;
      }
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = (): void => {
        if (this.themePreference() === 'auto') {
          this.colorSchemeTick.update((v) => v + 1);
        }
      };
      mq.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));
    });
  }

  /**
   * Inizializza lo store caricando i dati dal localStorage.
   * Se non c'è un device_id salvato, genera un fingerprint browser/macchina (DeviceIdService).
   */
  private initializeStore(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Carica i dati salvati
        if (parsed.token) this.token.set(parsed.token);
        if (parsed.user) this.user.set(parsed.user);
        if (parsed.username) this.username.set(parsed.username);
        if (parsed.rememberMe !== undefined) {
          this.rememberMe.set(parsed.rememberMe);
        }
        if (
          parsed.themePreference === 'auto' ||
          parsed.themePreference === 'light' ||
          parsed.themePreference === 'dark'
        ) {
          this.themePreference.set(parsed.themePreference);
        }
        
        // Fingerprint valido → riusa; placeholder/legacy (es. letterale `device_id`) → rigenera
        if (this.deviceIdService.isValidDeviceId(parsed.device_id)) {
          this.device_id.set(parsed.device_id);
        } else {
          const newDeviceId = this.deviceIdService.generateDeviceId();
          this.device_id.set(newDeviceId);
          this.saveToStorage();
        }
      } else {
        const newDeviceId = this.deviceIdService.generateDeviceId();
        this.device_id.set(newDeviceId);
        this.saveToStorage();
      }

    } catch (error) {
      Console.error('AppStore', { message: 'Errore nel caricamento dello store', error });
      const newDeviceId = this.deviceIdService.generateDeviceId();
      this.device_id.set(newDeviceId);
    }
  }

  /**
   * Configura la sincronizzazione automatica con il localStorage
   */
  private setupStorageSync(): void {
    effect(() => {
      // Sincronizza solo i campi selezionati con il localStorage
      const stateToSave = {
        token: this.token(),
        user: this.user(),
        username: this.username(),
        device_id: this.device_id(),
        rememberMe: this.rememberMe(),
        themePreference: this.themePreference()
      };
      
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        Console.error('AppStore', { message: 'Errore nel salvataggio dello store', error });
      }
    });

  }

  /**
   * Salva manualmente lo stato nel localStorage
   */
  private saveToStorage(): void {
    const stateToSave = {
      token: this.token(),
      user: this.user(),
      username: this.username(),
      device_id: this.device_id(),
      rememberMe: this.rememberMe(),
      themePreference: this.themePreference()
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      Console.error('AppStore', { message: 'Errore nel salvataggio dello store', error });
    }
  }

  /**
   * Imposta il token di autenticazione nello store.
   *
   * @param token Il token di autenticazione da salvare
   */
  setToken(token: IToken): void {
    this.token.set(token);
  }

  /**
   * Imposta i dati dell'utente nello store.
   * Converte automaticamente IUser in User se necessario.
   *
   * @param user I dati dell'utente da salvare
   */
  setUser(user: IUser | User): void {
    // Se è già un'istanza di User, usa direttamente
    if (user instanceof User) {
      this.user.set(user.toJSON());
    } else {
      this.user.set(user);
    }
  }

  /**
   * Restituisce sempre una vera istanza della classe User,
   * anche dopo la deserializzazione dal localStorage.
   */
  getUser(): User | null {
    const raw = this.user();
    if (!raw) return null;
    if (raw instanceof User) return raw;
    return new User(raw);
  }

  /**
   * Imposta lo username dell'utente nello store.
   *
   * @param username Lo username dell'utente da salvare
   */
  setUsername(username: string): void {
    this.username.set(username);
  }

  /**
   * Imposta l'ID del dispositivo nello store.
   * Il device_id viene salvato automaticamente nel localStorage tramite l'effect.
   *
   * @param deviceId L'ID del dispositivo da salvare
   */
  setDeviceId(deviceId: string): void {
    this.device_id.set(deviceId);
    // Il salvataggio avviene automaticamente tramite l'effect setupStorageSync()
  }

  /**
   * Pulisce tutti i dati di autenticazione e dello store.
   *
   * Resetta lo store al suo stato iniziale mantenendo però
   * device_id e hasSeenIntro per non perdere queste informazioni.
   * Pulisce esplicitamente anche il localStorage.
   */
  clearAuth(): void {
    // Se rememberMe è attivo, manteniamo rememberMe e username invece di pulire tutto
    const rememberMe = this.rememberMe();
    const username = this.username();
    
    // Salviamo device_id e hasSeenIntro per non perderli
    const currentDeviceId = this.device_id();
    const currentHasSeenIntro = this.hasSeenIntro();

    // Se rememberMe è attivo, manteniamo rememberMe e username nella chiave principale
    // La chiave principale è già univoca per ogni app/origine, quindi non verrà pulita quando l'app esterna fa logout
    if (rememberMe && username) {
      // Manteniamo rememberMe, username, device_id e hasSeenIntro nella chiave principale
      const stateToKeep = {
        rememberMe: rememberMe,
        username: username,
        device_id: currentDeviceId,
        hasSeenIntro: currentHasSeenIntro,
        themePreference: this.themePreference()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToKeep));
    } else {
      // Se rememberMe non è attivo, puliamo completamente il localStorage
      localStorage.removeItem(STORAGE_KEY);
    }

    // Poi resettiamo lo store mantenendo device_id, hasSeenIntro, e se rememberMe è attivo anche rememberMe e username
    this.token.set(null);
    this.user.set(null);
    this.activeRole.set(null);
    this.error.set(null);
    this.isLoading.set(false);
    this.profiles.set(null);
    this.device_id.set(currentDeviceId);
    this.hasSeenIntro.set(currentHasSeenIntro);
    
    // Se rememberMe è attivo, manteniamo rememberMe e username
    if (rememberMe && username) {
      // Manteniamo rememberMe e username per permettere il re-login
      // Non resettiamo questi valori
    } else {
      // Se rememberMe non è attivo, puliamo anche username e rememberMe
      this.username.set(null);
      this.rememberMe.set(false);
    }

    // Salva lo stato aggiornato (ma solo se rememberMe non è attivo, altrimenti abbiamo già salvato sopra)
    if (!rememberMe || !username) {
      this.saveToStorage();
    }
  }

  /**
   * Imposta il ruolo attivo
   */
  setActiveRole(role: RuoloAttivo): void {
    this.activeRole.set(role);
  }

  /**
   * Imposta lo stato di caricamento
   */
  setLoading(loading: boolean): void {
    this.isLoading.set(loading);
  }

  /**
   * Imposta un errore
   */
  setError(error: any): void {
    this.error.set(error);
  }

  /**
   * Imposta il flag hasSeenIntro
   */
  setHasSeenIntro(seen: boolean): void {
    this.hasSeenIntro.set(seen);
  }

  /**
   * Imposta il flag rememberMe
   * Il flag viene salvato automaticamente nel localStorage tramite l'effect.
   *
   * @param rememberMe Il valore del flag rememberMe da salvare
   */
  setRememberMe(rememberMe: boolean): void {
    this.rememberMe.set(rememberMe);
  }

  /**
   * Imposta i profili utente nello store.
   * I profili non vengono salvati nel localStorage perché vengono ricaricati ad ogni sessione.
   * Aggiorna anche i profili nell'utente corrente se presente.
   *
   * @param profiles Array di profili utente da salvare nello store
   */
  setProfiles(profiles: IProfilo[]): void {
    this.profiles.set(profiles);
    
    // Aggiorna anche i profili nell'utente corrente se presente
    const currentUser = this.user();
    if (currentUser) {
      const updatedUser = { ...currentUser, profili: profiles };
      this.user.set(updatedUser);
    }
  }
}
